import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { useState, useEffect } from 'react';
import { useCookies } from 'react-cookie';

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }

//? This can be used for testing purposes
// const oneMinuteInMilliseconds = 1000 * 60;

/**
 * @typedef {{[key: string]: any}} WrapperCookie
 */
/**
 * Create a single cookie which wraps other cookies as JSON
 *
 * @param {string} wrapperCookieName The name of the wrapper cookie which contains all relevant data
 * @param {number} [cookieExpirationTimeMs=31536000000] The time in milliseconds until the cookie expires
 * @returns {{actualCookie: WrapperCookie, transactionalState: WrapperCookie, setCookieInWrapper: Function, setMultipleCookiesInWrapper: Function, setAllCookiesInWrapper: Function, commitWrapperCookie: Function}}
 */
function useWrapperCookie(wrapperCookieName) {
  var _allCookies$wrapperCo, _allCookies$wrapperCo2;
  var cookieExpirationTimeMs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 3 * 30 * 24 * 60 * 60 * 1000;
  var _useCookies = useCookies([wrapperCookieName]),
    _useCookies2 = _slicedToArray(_useCookies, 2),
    allCookies = _useCookies2[0],
    setCookie = _useCookies2[1];
  var _useState = useState((_allCookies$wrapperCo = allCookies === null || allCookies === void 0 ? void 0 : allCookies[wrapperCookieName]) !== null && _allCookies$wrapperCo !== void 0 ? _allCookies$wrapperCo : {}),
    _useState2 = _slicedToArray(_useState, 2),
    cookieTransaction = _useState2[0],
    setCookieTransaction = _useState2[1];
  useEffect(function () {
    if (allCookies[wrapperCookieName] == null) {
      setCookie(wrapperCookieName, {}, {
        path: "/",
        expires: new Date(Date.now() + cookieExpirationTimeMs)
      });
    }
  }, [wrapperCookieName]);

  /**
   * Save a specific cookie to state
   *
   * @param {string} cookieName
   * @param {any} cookieValue
   */
  function setCookieInWrapper(cookieName, cookieValue) {
    var newWrapperCookie = _objectSpread(_objectSpread({}, cookieTransaction), {}, _defineProperty({}, cookieName.toLowerCase(), cookieValue));
    setCookieTransaction(newWrapperCookie);
  }

  /**
   * Save multiple cookies to state simultaneously.
   * This is necessary when saving multiple cookies in quick succession
   * to avoid issues with outdated state.
   *
   * @param {{[cookieName: string]: any}} cookieKeyValuePairs An object of key-value pairs representing the updated cookies
   * @param {boolean} commit to commit instantly
   */
  function setMultipleCookiesInWrapper(cookieKeyValuePairs) {
    var commit = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    var tempWrapperCookie = _objectSpread(_objectSpread({}, cookieTransaction), cookieKeyValuePairs);
    var wrapperCookieEntries = Object.keys(tempWrapperCookie).map(function (key) {
      return [key.toLowerCase(), tempWrapperCookie[key]];
    });
    var newWrapperCookie = Object.fromEntries(wrapperCookieEntries);
    setCookieTransaction(newWrapperCookie);
    if (commit) {
      //took from commitWrapperCookie
      setCookie(wrapperCookieName, _objectSpread(_objectSpread({}, allCookies[wrapperCookieName]), newWrapperCookie), {
        path: "/",
        expires: new Date(Date.now() + cookieExpirationTimeMs)
      });
    }
  }
  function acceptAllCookies() {
    var wrapperCookieEntries = Object.keys(cookieTransaction).map(function (key) {
      return [key.toLowerCase(), true];
    });
    setCookie(wrapperCookieName, Object.fromEntries(wrapperCookieEntries), {
      path: "/",
      expires: new Date(Date.now() + cookieExpirationTimeMs)
    });
  }

  /**
   * Commit the wrapper cookie and set it as an actual cookie
   */
  function commitWrapperCookie() {
    setCookie(wrapperCookieName, _objectSpread(_objectSpread({}, allCookies[wrapperCookieName]), cookieTransaction), {
      path: "/",
      expires: new Date(Date.now() + cookieExpirationTimeMs)
    });
  }

  /**
   * Save a specific cookie to actual cookie
   *
   * @param {string} cookieName
   * @param {any} cookieValue
   */
  function commitSpecificCookie(cookieName, cookieValue) {
    setCookie(wrapperCookieName, _objectSpread(_objectSpread({}, allCookies[wrapperCookieName]), {}, _defineProperty({}, cookieName.toLowerCase(), cookieValue)), {
      path: "/",
      expires: new Date(Date.now() + cookieExpirationTimeMs)
    });
  }
  return {
    actualCookie: (_allCookies$wrapperCo2 = allCookies === null || allCookies === void 0 ? void 0 : allCookies[wrapperCookieName]) !== null && _allCookies$wrapperCo2 !== void 0 ? _allCookies$wrapperCo2 : {},
    transactionalState: cookieTransaction,
    setCookieInWrapper: setCookieInWrapper,
    setMultipleCookiesInWrapper: setMultipleCookiesInWrapper,
    commitWrapperCookie: commitWrapperCookie,
    acceptAllCookies: acceptAllCookies,
    commitSpecificCookie: commitSpecificCookie
  };
}

export { useWrapperCookie as default };
//# sourceMappingURL=useWrapperCookie.js.map
